import { Combobox } from "@headlessui/react";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { useState, useMemo } from "react";
import { ILead } from "../../models/Lead";
import { TemplateType } from "../../models/TemplateEditor";
import classNames from "../../utils/classNames";
import useEmails from "../../data/useEmails";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useCustomerDetails from "../../data/useCustomerDetails";
import useTemplateEditor from "../../data/useTemplateEditor";
import formatDateTime from './../../utils/formatDateTime';
import { DateTime } from "luxon";
import CustomTinyMceEditor from "../shared/Inputs/CustomTinyMceEditor";


function applyMergeTags(body: string, lead: ILead, companyName: string, companyTelephone: string, companyEmail: string): string {
  const collectionDate = lead.vehicle?.scheduledCollectionOn
    ? formatDateTime(lead.vehicle.scheduledCollectionOn, DateTime.DATETIME_MED, "")
    : "";

  return body
    .replace(/\{\{ContactName\}\}/g, lead.contact?.fullname?.trim() ?? "")
    .replace(/\{\{CompanyName\}\}/g, companyName)
    .replace(/\{\{CollectionDateTime\}\}/g, collectionDate)
    .replace(/\{\{VehicleMake\}\}/g, lead.vehicle?.combinedMake ?? "")
    .replace(/\{\{VehicleModel\}\}/g, lead.vehicle?.modelRangeDescription?.toUpperCase() ?? "")
    .replace(/\{\{VehicleRegistration\}\}/g, lead.vehicle?.vrm ?? "")
    .replace(/\{\{CompanyTelephone\}\}/g, companyTelephone)
    .replace(/\{\{CompanyEmail\}\}/g, companyEmail);
}


export default function SendEmail({ lead, setOpen }: { lead: ILead, setOpen: any }) {
  const { sendEmail } = useEmails();
  const { saveHandlers } = useDefaultCRUDHandlers("Email sent");
  const [selected, setSelected] = useState<any>({ id: undefined });
  const [email, setEmail] = useState<string>(lead.contact?.email!);
  const [content, setContent] = useState<string>("");

  const { customer } = useCustomerDetails();
  const { templates } = useTemplateEditor();

  const emailTemplates = useMemo(() => {
    const companyName = customer.data?.data.name ?? "";
    const companyTelephone = customer.data?.data.telephone ?? "";
    const companyEmail = customer.data?.data.email ?? "";
    const allowedTitles = ["Enquiry Received", "Vehicle Collection", "No Contact", "Confirm Collection", "Wrong Number"];
    return templates
      .filter((t) => t.type === TemplateType.Email && allowedTitles.includes(t.title))
      .map((t) => {
        const body = t.childTemplate?.body ?? t.body;
        const title = t.childTemplate?.title ?? t.title;
        return {
          id: t.id,
          title,
          content: applyMergeTags(body, lead, companyName, companyTelephone, companyEmail),
        };
      });
  }, [templates, customer.data, lead]);

  const handleSetSelected = (template: any) => {
    setSelected(template);
    setContent(template.content);
  }

  const handleSendEmail = () => {
    var emailToSend = {
      ContactId: lead.contact?.id,
      Recipient: email,
      Body: content,
      Name: "",
      Subject: selected.title
    }

    sendEmail.mutate(emailToSend, {
      onSuccess: () => {
        setOpen(false);
        saveHandlers.onSuccess();
      },
      onError: () => {
        saveHandlers.onError();
      }
    })
  }

  return (
    <Combobox>
      {({ activeOption }) => (
        <>
          <Combobox.Options
            as="div"
            static
            className="flex divide-y divide-gray-100 flex-col sm:flex-row sm:divide-x h-full"
          >
            <div className="h-full min-w-0 h-80 flex-auto scroll-py-4 overflow-y-auto px-6 py-4">
              <div className="-mx-2 text-sm text-gray-700">
                {emailTemplates.map(
                  (template) => (
                    <Combobox.Option
                      as="div"
                      key={template.id}
                      value={template}
                      onClick={() => handleSetSelected(template)}
                      className={({ active }) =>
                        classNames(active || selected.id === template.id ? "bg-gray-100 text-gray-900" : "",
                          "flex cursor-pointer select-none items-center rounded-md p-2"
                        )
                      }
                    >
                      {({ active }) => (
                        <>
                          <span className="ml-3 flex-auto truncate">
                            {template.title}
                          </span>
                          {Boolean(active || selected.id === template.id) && (
                            <ChevronRightIcon
                              className="ml-3 h-5 w-5 flex-none text-gray-400"
                              aria-hidden="true"
                            />
                          )}
                        </>
                      )}
                    </Combobox.Option>
                  )
                )}
              </div>
            </div>

            {selected.id ? (
              <div className="h-full w-full flex-none grow flex-col divide-y divide-gray-100 overflow-y-auto flex sm:w-1/2">
                <div className="p-6">
                  <div className="mb-4">
                    <label>Email:</label>
                    <input value={email} onChange={((e) => setEmail(e.target.value))} type="text" className="text-sm w-full rounded-md border-gray-600" />
                  </div>

                  <div>
                    <label>Content:</label>
                  </div>
                  <CustomTinyMceEditor
                    mode="html"
                    content={content}
                    setContent={setContent}
                    mergeTags={[]}
                    height={380}
                    readonly
                  />
                </div>


                <div className="flex flex-auto flex-col justify-between p-6">
                  <button
                    onClick={() => handleSendEmail()}
                    type="button"
                    className="w-full rounded-md border border-transparent bg-primary-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  >
                    Send
                  </button>
                </div>
              </div>
            ) : (
              <div className="h-80 w-full sm:h-96 sm:w-1/2"></div>
            )}
          </Combobox.Options>
        </>
      )}
    </Combobox>
  )
}
